import { getDisplayVolume } from './commissionUtils';
import {
  signOdds,
  capitalize,
  getDrawLabel,
  convertAmericanToDecimal,
} from './utils';
import { useUserStore } from '@/stores/user';
// Split an owned aggregate price-feed level into other users' share and the
// user's own share. The others' share must carry no trace of the user's
// order: stamping put the user's REAL order id (and userOrderIDs) on the
// aggregate row, so a plain copy would let take/cancel flows that key off
// `id` target the user's own order through the "public volume" row. Revert
// its id to the synthetic aggregate id and strip every user-scoped field.
export function splitOwnAggregateOrder(order) {
  const levelVolume = Number(order.sumUntaken) || 0;
  const userVolume = Math.min(Number(order.userSumUntaken) || 0, levelVolume);
  const othersVolume = levelVolume - userVolume;
  // Level volume and the user's share are computed by different services
  // (price-feed aggregate vs user-feed order summary), so a fully-owned level
  // subtracts to float residue instead of exactly 0 — which rendered as a
  // phantom "$0.00" row under the user's own order. Nothing below a cent can
  // be a real order (open orders require sumUntaken >= 10), so a cent floor
  // also absorbs genuine sub-cent drift between the two feeds.
  const hasOthers = othersVolume >= 0.01;

  return {
    others:
      hasOthers
        ? {
            ...order,
            id: order.aggregateID ?? order.id,
            createdBy: undefined,
            isUsers: false,
            userOrderIDs: undefined,
            multipleUserOrders: undefined,
            userSumUntaken: undefined,
            gameStartExpiry: undefined,
            sumUntaken: othersVolume,
            bet: othersVolume,
          }
        : null,
    own:
      userVolume > 0
        ? {
            ...order,
            sumUntaken: userVolume,
            bet: userVolume,
          }
        : null,
  };
}

// this function is used to create bid and ask ladder for modals that use that component
export function formatBidAndAsk(ordersFromGame, league, betType) {
  const userStore = useUserStore();
  if (!Array.isArray(ordersFromGame)) ordersFromGame = [];

  // An aggregate price-feed row holds ALL volume at a price level, the
  // user's and other users' combined. Dropping it as a "user bet" would hide
  // other people's takeable volume, so split it: others' share stays in the
  // takeable ladder, the user's share is listed as their own bid, mirroring
  // the old per-order rows.
  const expandedOrders = [];
  ordersFromGame.forEach((order) => {
    const isOwnAggregate =
      order.priceFeedAggregate && order.createdBy === userStore.user.id;
    if (!isOwnAggregate) {
      expandedOrders.push(order);
      return;
    }
    const { others, own } = splitOwnAggregateOrder(order);
    if (others) expandedOrders.push(others);
    if (own) expandedOrders.push(own);
  });

  expandedOrders.forEach((order) => {
    if (order.volume !== 0) {
      // Commission is charged on min(taker risk, taker win). Legacy rows
      // carried the taker win as `bet` (the maker's stake); aggregate rows
      // have no per-order stake and set bet == sumUntaken, which made the
      // base always the risk and overstated displayed volume on
      // favourite-side levels. Derive the win from the odds instead.
      const takerWin = order.priceFeedAggregate
        ? order.sumUntaken * (convertAmericanToDecimal(order.odds) - 1)
        : order.bet;
      order.volume = getDisplayVolume(
        order.sumUntaken,
        Math.min(order.sumUntaken, takerWin),
        league,
        betType,
      );
    }
  });

  let orderLadder = [];
  let nonUserBets = expandedOrders.filter(
    (b) => b.createdBy !== userStore.user.id && b.odds !== undefined,
  );
  const uniqueOdds = nonUserBets
    .map((item) => item.odds)
    .filter((value, index, self) => self.indexOf(value) === index);

  // non user bets
  uniqueOdds.forEach((odds) => {
    const ladderWithSameOdd = nonUserBets.filter((item) => item.odds === odds);
    if (ladderWithSameOdd.length > 1) {
      const combinedVolume = ladderWithSameOdd.reduce(
        (total, current) => parseFloat(total) + parseFloat(current.volume),
        0,
      );
      const combinedBid = {
        ...ladderWithSameOdd[0],
        volume: combinedVolume,
      };
      orderLadder.push(combinedBid);
    } else {
      orderLadder.push(ladderWithSameOdd[0]);
    }
  });

  const auxOrderLadder = [];
  let cumulativeVolume = 0;

  for (let i = 0; i < orderLadder.length; i++) {
    cumulativeVolume += orderLadder[i].volume;
    auxOrderLadder.push({
      ...orderLadder[i], // Spread the original object
      cumulativeVolume: cumulativeVolume, // Add the new cumulative volume property
    });
  }
  orderLadder = auxOrderLadder;
  // add in the users bids
  const userBets = expandedOrders.filter(
    (b) => b.createdBy === userStore.user.id && b.odds !== undefined,
  );
  userBets.forEach((bid) => orderLadder.push(bid));

  // sort the bid ladder
  orderLadder.sort((a, b) => {
    if (a.odds === b.odds) {
      if (a.isUsers && !b.isUsers) return -1;
      if (!a.isUsers && b.isUsers) return 1;
      const aCreatedAt = a.createdAt ? new Date(a.createdAt) : 0;
      const bCreatedAt = b.createdAt ? new Date(b.createdAt) : 0;
      return bCreatedAt - aCreatedAt;
    }
    return b.odds - a.odds;
  });

  return orderLadder;
}

export function getSideStringForModals(
  game,
  session,
  type,
  number,
  showodds = true,
) {
  let sideString;
  if (type === 'moneyline') {
    if (game.league.toLowerCase().includes('future')) {
      const team = game.participants.find(
        (p) => p.id === session.participantID,
      );
      sideString = `${team.longName} ${team.futuresSide}`;
    } else {
      sideString = game.participants.find(
        (p) => p.id === session.participantID,
      ).longName;
    }
  } else if (type === 'spread') {
    const team = game.participants.find((p) => p.id === session.participantID);
    sideString = `${team.longName}  ${signOdds(number)}`;
  } else if (type === 'total') {
    sideString = `${capitalize(session.OU)} ${number}`;
  } else if (type === 'moneyline1x2') {
    const side = session.side === 'yes' ? 'YES' : 'NO';
    if (session.market !== 'draw') {
      if (game.league.toLowerCase().includes('future')) {
        const team = game.participants.find((p) => p.id === session.market);
        sideString = `${team.longName} ${team.futuresSide}`;
      } else {
        sideString = `${side} ${
          game.participants.find((p) => p.id === session.market).longName
        }`;
      }
    } else {
      sideString = `${side} ${getDrawLabel(game)}`;
    }
  }
  return sideString;
}

export function getBestOddsWithNumber(selectedGame, sideType, number) {
  const getFirstOdds = (array) => {
    if (Array.isArray(array) && array.length > 0) {
      return array[0].odds || 0; // Return odds if available, otherwise 0
    }
    return 0;
  };
  if (sideType === 'awayMoneylines') {
    return getFirstOdds(selectedGame.awayMoneylines);
  } else if (sideType === 'homeMoneylines') {
    return getFirstOdds(selectedGame.homeMoneylines);
  } else if (sideType.homeAwayDraw === 'awayMoneylines1x2') {
    if (sideType.side === 'yes') {
      return getFirstOdds(selectedGame.awayMoneylines1x2?.awayYes);
    } else if (sideType.side === 'no') {
      return getFirstOdds(selectedGame.awayMoneylines1x2?.awayNo);
    }
  } else if (sideType.homeAwayDraw === 'homeMoneylines1x2') {
    if (sideType.side === 'yes') {
      return getFirstOdds(selectedGame.homeMoneylines1x2?.homeYes);
    } else if (sideType.side === 'no') {
      return getFirstOdds(selectedGame.homeMoneylines1x2?.homeNo);
    }
  } else if (sideType.homeAwayDraw === 'draw1x2') {
    if (sideType.side === 'yes') {
      return getFirstOdds(selectedGame.draw1x2?.drawYes);
    } else if (sideType.side === 'no') {
      return getFirstOdds(selectedGame.draw1x2?.drawNo);
    }
  }
  // Default case
  return getFirstOdds(selectedGame?.[sideType]?.[number]);
}

export function noDecimal(value) {
  return value.toFixed(0);
}